/**
 * Whether the order is a container order (not pod, box, or Howden).
 *
 * @param {object} orderDetail - Order detail payload.
 * @returns {boolean} True when `is_container` resolves to 1.
 */
export const isContainerOrder = (orderDetail) =>
  Number(orderDetail?.is_container) === 1;

/**
 * Extract the lockout-grace data object from an API response.
 *
 * @param {object} response - Axios response object.
 * @returns {object} Grace settings payload from the API.
 */
export const getLockoutGracePayload = (response) =>
  response?.data?.data ?? response?.data ?? {};

/**
 * Parse grace days from a lockout-grace API response.
 *
 * @param {object} response - Axios response object.
 * @returns {number|null} Parsed grace days, or null when unset.
 */
export const parseLockoutGraceDays = (response) => {
  const payload = getLockoutGracePayload(response);
  const days = payload?.lockout_grace_days ?? payload?.days;

  if (days === null || days === undefined || days === "") {
    return null;
  }

  const parsed = Number(days);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : null;
};

/**
 * Whether the order has an active manual lockout grace override.
 *
 * @param {object} response - Axios response object.
 * @returns {boolean} True when `manual_override_active` is set.
 */
export const hasManualLockoutGraceOverride = (response) =>
  getLockoutGracePayload(response)?.manual_override_active === true;

/**
 * Build the PUT payload for updating lockout grace days.
 *
 * @param {number} days - Grace period in days.
 * @returns {{ days: number }} API request body.
 */
export const buildLockoutGracePayload = (days) => ({
  days,
});

/**
 * Whether a lockout-grace GET failed because no grace period exists yet.
 *
 * @param {*} error - Axios error object.
 * @returns {boolean} True when the server reports no grace period.
 */
export const isLockoutGraceNotFound = (error) => error?.response?.status === 404;

/**
 * Format grace days for display in the billing card.
 *
 * @param {number|null|undefined} graceDays - Grace period in days.
 * @returns {string} Formatted label or "-" when unset.
 */
export const formatGracePeriodLabel = (graceDays) => {
  if (graceDays == null) {
    return "-";
  }

  return `${graceDays} ${graceDays === 1 ? "day" : "days"}`;
};
